import React, { useMemo, useState } from 'react';
import { Book, BookStatus, STANDARD_ITEM_TYPES } from '../objects/Book';

export interface NewParentSpec {
  title: string;
  author?: string;
  itemType: string;
}

export interface BookFormSubmission {
  title: string;
  author?: string;
  status: BookStatus;
  itemType: string;
  isSection: boolean;
  parentUuid?: string;
  newParent?: NewParentSpec;
  sectionDescription?: string;
  note?: string;
}

interface BookFormProps {
  books: Book[];
  onCreate: (data: BookFormSubmission) => Promise<void> | void;
}

const parentLabel = (book: Book): string =>
  book.author ? `${book.title} — ${book.author}` : book.title;

const findExistingParent = (text: string, parents: Book[]): Book | undefined => {
  const trimmed = text.trim().toLowerCase();
  if (!trimmed) return undefined;
  return parents.find((p) => {
    if (parentLabel(p).toLowerCase() === trimmed) return true;
    if (p.title.toLowerCase() === trimmed) return true;
    return false;
  });
};

const BookForm: React.FC<BookFormProps> = ({ books, onCreate }) => {
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [status, setStatus] = useState<BookStatus>('done');
  const [itemType, setItemType] = useState('book');
  const [customType, setCustomType] = useState('');
  const [isSection, setIsSection] = useState(false);
  const [parentText, setParentText] = useState('');
  const [newParentAuthor, setNewParentAuthor] = useState('');
  const [sectionDescription, setSectionDescription] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const eligibleParents = useMemo(() => books.filter((b) => !b.is_section), [books]);
  const matchedParent = useMemo(
    () => findExistingParent(parentText, eligibleParents),
    [parentText, eligibleParents]
  );
  const hasParentText = parentText.trim().length > 0;
  const isCreatingNewParent = isSection && hasParentText && !matchedParent;

  const resetForm = () => {
    setTitle('');
    setAuthor('');
    setStatus('done');
    setItemType('book');
    setCustomType('');
    setIsSection(false);
    setParentText('');
    setNewParentAuthor('');
    setSectionDescription('');
    setNote('');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!title.trim()) return;

    const resolvedType = itemType === '__custom' ? customType.trim().toLowerCase() : itemType;
    if (!resolvedType) return;

    let parentUuid: string | undefined;
    let newParent: NewParentSpec | undefined;
    if (isSection) {
      if (!hasParentText) return;
      if (matchedParent) {
        parentUuid = matchedParent.uuid;
      } else {
        newParent = {
          title: parentText.trim(),
          author: newParentAuthor.trim() || undefined,
          itemType: 'book',
        };
      }
    }

    setSubmitting(true);
    try {
      await onCreate({
        title: title.trim(),
        author: author.trim() || undefined,
        status,
        itemType: resolvedType,
        isSection,
        parentUuid,
        newParent,
        sectionDescription: isSection ? sectionDescription.trim() || undefined : undefined,
        note: note.trim() || undefined,
      });
      resetForm();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="grid" style={{ gap: '1rem' }}>
      <label className="field">
        <span className="field-label">Title</span>
        <input
          className="input"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder="What did you read?"
          required
        />
      </label>

      <label className="field">
        <span className="field-label">Author (optional)</span>
        <input
          className="input"
          value={author}
          onChange={(event) => setAuthor(event.target.value)}
          placeholder="Add the author name"
        />
      </label>

      <label className="field">
        <span className="field-label">Status</span>
        <select className="select" value={status} onChange={(event) => setStatus(event.target.value as BookStatus)}>
          <option value="done">Done</option>
          <option value="reading">Reading</option>
          <option value="occasional reference">Occasional reference</option>
          <option value="in queue">In queue</option>
          <option value="abandoned">Abandoned</option>
          <option value="probably not">Probably not</option>
          <option value="won't read">Won't read</option>
        </select>
      </label>

      <label className="field">
        <span className="field-label">Type</span>
        <select
          className="select"
          value={itemType}
          onChange={(event) => {
            setItemType(event.target.value);
            if (event.target.value !== '__custom') {
              setCustomType('');
            }
          }}
        >
          {STANDARD_ITEM_TYPES.map((t) => (
            <option key={t} value={t}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </option>
          ))}
          <option value="__custom">Other…</option>
        </select>
      </label>

      {itemType === '__custom' && (
        <label className="field">
          <span className="field-label">Custom type</span>
          <input
            className="input"
            value={customType}
            onChange={(event) => setCustomType(event.target.value)}
            placeholder="e.g. graphic novel, libretto"
            required
          />
        </label>
      )}

      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={isSection}
          onChange={(e) => {
            setIsSection(e.target.checked);
            if (!e.target.checked) {
              setParentText('');
              setNewParentAuthor('');
              setSectionDescription('');
            }
          }}
        />
        <span className="field-label">This is a section of another work</span>
      </label>

      {isSection && (
        <>
          <label className="field">
            <span className="field-label">Parent work</span>
            <input
              className="input"
              list="parent-work-options"
              value={parentText}
              onChange={(e) => setParentText(e.target.value)}
              placeholder="Type a title — pick an existing work or add a new one"
              required
              autoComplete="off"
            />
            <datalist id="parent-work-options">
              {eligibleParents.map((b) => (
                <option key={b.uuid} value={parentLabel(b)} />
              ))}
            </datalist>
            {hasParentText && (
              <span className="helper-text" style={{ marginTop: '0.25rem' }}>
                {matchedParent
                  ? `Linking to existing: ${parentLabel(matchedParent)}`
                  : 'No match — a new parent work will be created.'}
              </span>
            )}
          </label>

          {isCreatingNewParent && (
            <label className="field">
              <span className="field-label">New parent author (optional)</span>
              <input
                className="input"
                value={newParentAuthor}
                onChange={(e) => setNewParentAuthor(e.target.value)}
                placeholder="Author of the larger work"
              />
            </label>
          )}

          <label className="field">
            <span className="field-label">Section description</span>
            <input
              className="input"
              value={sectionDescription}
              onChange={(e) => setSectionDescription(e.target.value)}
              placeholder='e.g. "Preface", "Chapters 1-3"'
            />
          </label>
        </>
      )}

      <label className="field">
        <span className="field-label">Note (optional)</span>
        <textarea
          className="textarea"
          rows={3}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Jot down a thought while it's fresh"
        />
      </label>

      <button className="button" type="submit" disabled={submitting}>
        {submitting ? 'Adding…' : 'Add item'}
      </button>
    </form>
  );
};

export default BookForm;
